// ============================================================
// CLUBHUB – Nachrichten: Platzhalter
// ============================================================
// Die Felder, die eine Nachricht je Empfaenger einsetzt. EINE Liste
// fuer die Knoepfe im Editor, die Vorschau und den Versand. Rein —
// laeuft im Browser (Vorschau) wie auf dem Server (Versand).
//
// Geschrieben wird `{{vorname}}`: doppelte Klammern, klein, ohne
// Leerzeichen. Ein Tippfehler bleibt stehen und wird im Editor als
// unbekannt gemeldet, statt still zu verschwinden.
// ============================================================
import type { ContactView, FeeTypeView } from "@/lib/views";
import { FEE_INTERVAL, SALUTATION } from "@/lib/labels";
import { formatCurrency } from "@/lib/utils";

export type PlatzhalterSchluessel =
  | "anrede"
  | "vorname"
  | "nachname"
  | "beitrag"
  | "offener_betrag"
  | "mitgliedsnummer"
  | "verein";

export const PLATZHALTER: { schluessel: PlatzhalterSchluessel; label: string; hinweis: string }[] = [
  { schluessel: "anrede", label: "Anrede", hinweis: "„Liebe Lena“, „Lieber Max“ — bei Firmen „Sehr geehrte Damen und Herren“" },
  { schluessel: "vorname", label: "Vorname", hinweis: "Vorname der Person" },
  { schluessel: "nachname", label: "Nachname", hinweis: "Nachname der Person" },
  { schluessel: "beitrag", label: "Beitrag", hinweis: "Der zugeordnete Beitrag, etwa „72,00 € jährlich“" },
  { schluessel: "offener_betrag", label: "Offener Betrag", hinweis: "Was aus Rechnungen noch offen ist" },
  { schluessel: "mitgliedsnummer", label: "Mitgliedsnummer", hinweis: "Leer bei Kontakten ohne Mitgliedschaft" },
  { schluessel: "verein", label: "Verein", hinweis: "Der Name des Vereins" },
];

const MUSTER = /\{\{\s*([a-z_äöü]+)\s*\}\}/gi;
const BEKANNT = new Set<string>(PLATZHALTER.map((p) => p.schluessel));

/**
 * Euro → „72,00 €“. Die Ansichtstypen tragen Euro (Schema: Decimal),
 * `formatCurrency` erwartet Cent (Fassung aus dem Agent).
 */
export function euro(betrag: number): string {
  return formatCurrency(Math.round(betrag * 100));
}

export function anredeFuer(c: ContactView): string {
  if (c.kind === "ORGANIZATION" || c.salutation === "FIRMA") return "Sehr geehrte Damen und Herren";
  const name = c.firstName ?? c.lastName ?? "";
  if (c.salutation === "FRAU") return `Liebe ${name}`;
  if (c.salutation === "HERR") return `Lieber ${name}`;
  return `Hallo ${name}`;
}

export function beitragText(fee: FeeTypeView | undefined): string {
  if (!fee) return "";
  if (fee.amount === 0) return "beitragsfrei";
  return `${euro(fee.amount)} ${FEE_INTERVAL[fee.interval]}`;
}

/** Die Werte einer Person — fehlt etwas, steht dort ein leerer Text. */
export function werteFuer(
  c: ContactView,
  ctx: { verein: string; beitraege: FeeTypeView[] },
): Record<PlatzhalterSchluessel, string> {
  const fee = c.feeTypeId ? ctx.beitraege.find((f) => f.id === c.feeTypeId) : undefined;
  return {
    anrede: anredeFuer(c),
    vorname: c.firstName ?? c.organizationName ?? "",
    nachname: c.lastName ?? "",
    beitrag: beitragText(fee),
    offener_betrag: euro(c.openAmount),
    mitgliedsnummer: c.memberNumber ?? "",
    verein: ctx.verein,
  };
}

/** Setzt die Werte ein. Unbekannte Platzhalter bleiben, wie sie sind. */
export function einsetzen(text: string, werte: Partial<Record<string, string>>): string {
  return text.replace(MUSTER, (ganz, name: string) => {
    const s = name.toLowerCase();
    return BEKANNT.has(s) ? (werte[s] ?? "") : ganz;
  });
}

/** Welche bekannten Platzhalter der Text benutzt. */
export function benutzte(text: string): PlatzhalterSchluessel[] {
  const gefunden = new Set<PlatzhalterSchluessel>();
  for (const m of text.matchAll(MUSTER)) {
    const s = m[1]!.toLowerCase();
    if (BEKANNT.has(s)) gefunden.add(s as PlatzhalterSchluessel);
  }
  return [...gefunden];
}

/** Platzhalter, die es nicht gibt — Tippfehler wie `{{vornahme}}`. */
export function unbekannte(text: string): string[] {
  const gefunden = new Set<string>();
  for (const m of text.matchAll(MUSTER)) {
    const s = m[1]!.toLowerCase();
    if (!BEKANNT.has(s)) gefunden.add(m[0]);
  }
  return [...gefunden];
}

/**
 * Fuer wie viele Empfaenger ein benutzter Platzhalter leer bliebe —
 * „{{mitgliedsnummer}} ist bei 3 Empfängern leer“.
 */
export function leereWerte(
  text: string,
  kontakte: ContactView[],
  ctx: { verein: string; beitraege: FeeTypeView[] },
): { schluessel: PlatzhalterSchluessel; anzahl: number }[] {
  const benutzt = benutzte(text);
  if (benutzt.length === 0) return [];
  const werte = kontakte.map((c) => werteFuer(c, ctx));
  return benutzt
    .map((s) => ({ schluessel: s, anzahl: werte.filter((w) => !w[s].trim()).length }))
    .filter((x) => x.anzahl > 0);
}

/** Die Zeilen des Anschriftfelds (DIN 5008): Anrede, Name, Strasse, Ort. */
export function anschriftZeilen(c: ContactView): string[] {
  if (c.kind === "ORGANIZATION") return [c.organizationName ?? c.displayName, c.street ?? "", `${c.zip ?? ""} ${c.city ?? ""}`.trim()];
  const anrede = c.salutation && c.salutation !== "KEINE" && c.salutation !== "DIVERS" ? SALUTATION[c.salutation] : null;
  const name = [c.title, c.firstName, c.lastName].filter(Boolean).join(" ");
  return [...(anrede ? [anrede] : []), name, c.street ?? "", `${c.zip ?? ""} ${c.city ?? ""}`.trim()];
}
